import { MeasureIcon } from '@principle-theorem/principle-core/interfaces';
import { MeasurePath } from '../data-accessor-factory';
import {
  CanDoAllProperty,
  CanQueryByTimestampProperty,
  propertyExistsFilterAccessor,
} from '../measure-properties';
import { MeasurePropertyFactory } from '../measure-property-factory';
import type { PatientDimensionMeasures } from './patient-dimension';

type RecallTrack = 'recall' | 'secondaryRecall';

/**
 * Namespace of per-track recall measures. One instance per track:
 * `patient.recall` uses `labelPrefix = 'Recall'` and
 * `patient.secondaryRecall` uses `labelPrefix = 'Secondary Recall'`.
 *
 * BigQuery paths follow the on-disk shape (`recall.*` / `secondaryRecall.*`)
 * defined by `IBigQueryPatientRecall`; `metadata.id` mirrors the getter
 * position so the two match the reporting API surface.
 */
export class PatientRecallMeasures {
  constructor(
    private parent: PatientDimensionMeasures,
    private track: RecallTrack,
    private labelPrefix: string,
  ) {}

  get nextRecallDue(): PatientNextRecallDueMeasures {
    return new PatientNextRecallDueMeasures(
      this.parent,
      this.track,
      this.labelPrefix,
    );
  }

  get periodInMonths(): CanDoAllProperty {
    const measure = this.parent.measureRef(this._bqPath('periodInMonths'));
    return MeasurePropertyFactory.number(
      {
        id: this._measureId('periodInMonths'),
        label: `${this.labelPrefix} Period (Months)`,
        summary: `The ${this.labelPrefix.toLowerCase()} period for the patient, in months`,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get periodIsExplicit(): CanDoAllProperty {
    const measure = this.parent.measureRef(this._bqPath('periodIsExplicit'));
    return MeasurePropertyFactory.boolean(
      {
        id: this._measureId('periodIsExplicit'),
        label: `Has Personalised ${this.labelPrefix} Period`,
        summary: `Whether the patient has a personalised ${this.labelPrefix.toLowerCase()} period or not`,
        icon: MeasureIcon.Checkbox,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get lastCompletedAt(): CanQueryByTimestampProperty {
    const measure = this.parent.measureRef(
      MeasurePath.timestamp(this._bqPath('lastCompletedAt')),
    );
    return MeasurePropertyFactory.timestamp(
      {
        id: this._measureId('lastCompletedAt'),
        label: `Last ${this.labelPrefix} Date`,
        summary: `Date that the patient last had a ${this.labelPrefix.toLowerCase()} appointment`,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get hasNextRecallAppointment(): CanDoAllProperty {
    const measure = this.parent.measureRef(
      this._bqPath('nextRecallAppointment'),
    );
    return MeasurePropertyFactory.propertyExists(
      {
        id: this._measureId('hasNextRecallAppointment'),
        label: `Has ${this.labelPrefix} Appointment Booked`,
        summary: `Whether the patient has a ${this.labelPrefix.toLowerCase()} appointment booked or not`,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
      propertyExistsFilterAccessor(measure.factPropertyPath),
    );
  }

  private _measureId(field: string): string {
    return `${this.parent.id}.${this.track}.${field}`;
  }

  private _bqPath(field: string): string {
    return `${this.track}.${field}`;
  }
}

/**
 * Sub-namespace under `PatientRecallMeasures.nextRecallDue`.
 */
export class PatientNextRecallDueMeasures {
  constructor(
    private parent: PatientDimensionMeasures,
    private track: RecallTrack,
    private labelPrefix: string,
  ) {}

  get isExplicit(): CanDoAllProperty {
    const measure = this.parent.measureRef(this._bqPath('isExplicit'));
    return MeasurePropertyFactory.boolean(
      {
        id: this._measureId('isExplicit'),
        label: `Next ${this.labelPrefix} Due Date Was Set Manually`,
        summary: `Whether the next ${this.labelPrefix.toLowerCase()} due date was set manually or not`,
        icon: MeasureIcon.Checkbox,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get isSet(): CanDoAllProperty {
    const measure = this.parent.measureRef(this._bqPath('isSet'));
    return MeasurePropertyFactory.boolean(
      {
        id: this._measureId('isSet'),
        label: `Has Next ${this.labelPrefix} Due`,
        summary: `Whether the patient has a next ${this.labelPrefix.toLowerCase()} due date or not`,
        icon: MeasureIcon.Checkbox,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get dueAt(): CanQueryByTimestampProperty {
    const measure = this.parent.measureRef(
      MeasurePath.timestamp(this._bqPath('dueAt')),
    );
    return MeasurePropertyFactory.timestamp(
      {
        id: this._measureId('dueAt'),
        label: `Next ${this.labelPrefix} Due`,
        summary: `Date that the patient is next due for a ${this.labelPrefix.toLowerCase()} appointment`,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  private _measureId(field: string): string {
    return `${this.parent.id}.${this.track}.nextRecallDue.${field}`;
  }

  private _bqPath(field: string): string {
    return `${this.track}.nextRecallDue.${field}`;
  }
}

/**
 * Aggregate recall measures spanning primary + secondary tracks.
 *
 * Both the TypeScript API and the underlying BigQuery paths are nested
 * under `combinedRecall`, matching the field names declared on
 * `IBasePatientDimension.combinedRecall`.
 */
export class PatientCombinedRecallMeasures {
  constructor(private parent: PatientDimensionMeasures) {}

  get nextRecallDueAt(): CanQueryByTimestampProperty {
    const measure = this.parent.measureRef(
      MeasurePath.timestamp(this._bqPath('nextRecallDueAt')),
    );
    return MeasurePropertyFactory.timestamp(
      {
        id: this._measureId('nextRecallDueAt'),
        label: 'Earliest Recall Due',
        summary: 'Earliest recall due date across primary and secondary tracks',
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get lastRecallCompletedAt(): CanQueryByTimestampProperty {
    const measure = this.parent.measureRef(
      MeasurePath.timestamp(this._bqPath('lastRecallCompletedAt')),
    );
    return MeasurePropertyFactory.timestamp(
      {
        id: this._measureId('lastRecallCompletedAt'),
        label: 'Latest Recall Completed',
        summary:
          'Latest recall completed date across primary and secondary tracks',
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get hasNextRecallAppointment(): CanDoAllProperty {
    const measure = this.parent.measureRef(
      this._bqPath('hasNextRecallAppointment'),
    );
    return MeasurePropertyFactory.boolean(
      {
        id: this._measureId('hasNextRecallAppointment'),
        label: 'Has Any Recall Appointment Booked',
        summary:
          'Whether the patient has any recall appointment booked across primary and secondary tracks',
        icon: MeasureIcon.Checkbox,
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  get nextRecallSourceTrack(): CanDoAllProperty {
    const measure = this.parent.measureRef(
      this._bqPath('nextRecallSourceTrack'),
    );
    return MeasurePropertyFactory.string(
      {
        id: this._measureId('nextRecallSourceTrack'),
        label: 'Earliest Recall Source Track',
        summary:
          'Source track (Primary or Secondary) of the earliest recall due',
      },
      measure,
      this.parent.buildQuery().attributes([measure.attributePath]).get(),
    );
  }

  private _measureId(field: string): string {
    return `${this.parent.id}.combinedRecall.${field}`;
  }

  private _bqPath(field: string): string {
    return `combinedRecall.${field}`;
  }
}
